import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmProvider } from '@/components/ConfirmProvider';

// Pages
import DashboardPage from '@/pages/DashboardPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import SettingsPage from '@/pages/SettingsPage';
import AccountPage from '@/pages/AccountPage';
import SecurityPage from '@/pages/SecurityPage';
import SubscriptionPage from '@/pages/SubscriptionPage';
import ChoosePlanPage from '@/pages/ChoosePlanPage';
import BillingSuccessPage from '@/pages/BillingSuccessPage';
import TeamSetupPage from '@/pages/TeamSetupPage';
import TeamSettingsPage from '@/pages/TeamSettingsPage';
import TeamsPage from '@/pages/TeamsPage';
import InvitationRegisterPage from '@/pages/InvitationRegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DangerPage from '@/pages/DangerPage';
import PublicAnalyticsPage from '@/pages/PublicAnalyticsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== AUTH CONTEXT ==============
const AuthContext = ({ children }) => {
	const [user, setUser] = useState(null);
	const [token, setToken] = useState(localStorage.getItem('token'));
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (token) {
			axios.get(`${API}/auth/me`, {
				headers: { Authorization: `Bearer ${token}` }
			})
				.then(res => {
					setUser(res.data);
					setLoading(false);
				})
				.catch(() => {
					localStorage.removeItem('token');
					setToken(null);
					setLoading(false);
				});
		} else {
			setLoading(false);
		}
	}, [token]);

	const login = async (email, password) => {
		const res = await axios.post(`${API}/auth/login`, { email, password });
		localStorage.setItem('token', res.data.token);
		setToken(res.data.token);
		setUser(res.data.user);
		return res.data;
	};

	const signup = async (email, password, fullName) => {
		const res = await axios.post(`${API}/auth/signup`, { email, password, name: fullName });
		localStorage.setItem('token', res.data.token);
		setToken(res.data.token);
		setUser(res.data.user);
		return res.data;
	};

	const logout = () => {
		localStorage.removeItem('token');
		setToken(null);
		setUser(null);
	};

	return children({ user, token, loading, login, signup, logout });
};

// ============== AUTH PAGE ==============
const AuthPage = ({ login, signup }) => {
	const [isLogin, setIsLogin] = useState(true);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [fullName, setFullName] = useState('');
	const [loading, setLoading] = useState(false);
	// Terms-of-service acceptance — required on signup, ignored on login.
	// Reset whenever the user toggles between modes so the previous state
	// doesn't leak across.
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const navigate = useNavigate();

	const handleSubmit = async (e) => {
		e.preventDefault();
		// Block signup early if the terms box isn't checked. The backend
		// doesn't enforce this (the checkbox is a UX/legal display, not a
		// security gate) so the form-level guard is what matters here.
		if (!isLogin && !acceptedTerms) {
			toast.error('Please accept the Terms of Service to create an account');
			return;
		}
		setLoading(true);
		try {
			if (isLogin) {
				await login(email, password);
				toast.success('Login successful!');
			} else {
				await signup(email, password, fullName);
				toast.success('Account created successfully!');
			}
		} catch (err) {
			// Handle different error formats from backend
			let errorMessage = 'Authentication failed';
			
			if (err.response?.data?.detail) {
				const detail = err.response.data.detail;
				// Handle array of validation errors
				if (Array.isArray(detail)) {
					errorMessage = detail.map(e => e.msg || e.message).join(', ');
				} 
				// Handle string error
				else if (typeof detail === 'string') {
					errorMessage = detail;
				}
				// Handle object error
				else if (typeof detail === 'object' && detail.msg) {
					errorMessage = detail.msg;
				}
			}
			
			toast.error(errorMessage);
		}
		setLoading(false);
	};

	return (
		<div className="auth-page-wrapper">
			<div className="auth-container-new">
				<div className="auth-card-new">
					{/* Illustrated hero — coloured top of the card with the brand */}
					<div className="auth-hero">
						<span className="auth-hero__star auth-hero__star--1" />
						<span className="auth-hero__star auth-hero__star--2" />
						<span className="auth-hero__star auth-hero__star--3" />
						<span className="auth-hero__star auth-hero__star--4" />
						<span className="auth-hero__star auth-hero__star--5" />
						<div className="auth-hero__brand">
							<img className="auth-hero__logo" src="/images/orbit-logo.svg" alt="Orbit" />
							<span className="auth-hero__name">Orbit Analytics</span>
						</div>
						{/* Soft cloud blobs at the bottom of the hero */}
						<span className="auth-hero__cloud auth-hero__cloud--left" />
						<span className="auth-hero__cloud auth-hero__cloud--right" />
						<span className="auth-hero__cloud auth-hero__cloud--center" />
					</div>

					<div className="auth-header-new">
						<h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
						<p>{isLogin ? 'Sign in to access your dashboard' : 'Start tracking your website analytics'}</p>
					</div>

					<form onSubmit={handleSubmit} className="auth-form-new">
						{!isLogin && (
							<div className="form-group">
								<label>Full Name</label>
								<Input
									type="text"
									placeholder="John Doe"
									value={fullName}
									onChange={(e) => setFullName(e.target.value)}
									required={!isLogin}
									className="auth-input"
								/>
							</div>
						)}
						<div className="form-group">
							<label>Email Address</label>
							<Input
								type="email"
								placeholder="you@example.com"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								className="auth-input"
							/>
						</div>
						<div className="form-group">
							<div className="label-row">
								<label>Password</label>
								{isLogin && (
									<button
										type="button"
										onClick={() => navigate('/forgot-password')}
										className="forgot-password-link"
									>
										Forgot password?
									</button>
								)}
							</div>
							<div style={{ position: 'relative' }}>
								<Input
									type={showPassword ? 'text' : 'password'}
									placeholder="••••••••"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
									className="auth-input"
									style={{ paddingRight: '2.5rem' }}
								/>
								<button
									type="button"
									onClick={() => setShowPassword((v) => !v)}
									aria-label={showPassword ? 'Hide password' : 'Show password'}
									tabIndex={-1}
									style={{
										position: 'absolute',
										right: '0.75rem',
										top: '50%',
										transform: 'translateY(-50%)',
										background: 'transparent',
										border: 'none',
										padding: 0,
										cursor: 'pointer',
										display: 'flex',
										alignItems: 'center',
										color: '#6b7280',
									}}
								>
									{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
								</button>
							</div>
						</div>
						{!isLogin && (
							// Inline-style flexbox so we override the column layout that
							// .form-group applies to the rest of the form fields. flexDirection
							// is the key — without it, the checkbox stacks above the label.
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									gap: '0.5rem',
									marginBottom: '0.5rem',
								}}
							>
								<input
									id="auth-accept-terms"
									type="checkbox"
									checked={acceptedTerms}
									onChange={(e) => setAcceptedTerms(e.target.checked)}
									style={{
										width: '1rem',
										height: '1rem',
										cursor: 'pointer',
										flex: '0 0 auto',
										margin: 0,
									}}
								/>
								<label
									htmlFor="auth-accept-terms"
									style={{
										fontSize: '0.875rem',
										color: '#4b5563',
										cursor: 'pointer',
										lineHeight: 1.4,
										margin: 0,
										whiteSpace: 'nowrap',
									}}
								>
									I agree to the{' '}
									<a
										href="https://helloorbit.com/terms-of-services"
										target="_blank"
										rel="noopener noreferrer"
										style={{ color: '#4f46e5', textDecoration: 'underline' }}
									>
										Terms of Service
									</a>
								</label>
							</div>
						)}
						{/* Inline helper text — explains why the Create Account button is
						    disabled. Auto-hides the moment the box is ticked. Only renders
						    on the signup view, never on Sign In. */}
						{!isLogin && !acceptedTerms && (
							<div
								role="alert"
								style={{
									fontSize: '0.8125rem',
									color: '#dc2626',
									marginTop: '-0.25rem',
									marginBottom: '0.25rem',
									lineHeight: 1.4,
								}}
							>
								Please accept the Terms of Service to create an account.
							</div>
						)}
						<Button
							type="submit"
							disabled={loading || (!isLogin && !acceptedTerms)}
							className="auth-submit-btn"
						>
							{loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
						</Button>
					</form>

					<div className="auth-footer">
						<p>
							{isLogin ? "Don't have an account? " : 'Already have an account? '}
							<button
								onClick={() => {
									setIsLogin(!isLogin);
									// Reset terms state on mode toggle so checked state doesn't leak.
									setAcceptedTerms(false);
								}}
								className="auth-switch-link"
							>
								{isLogin ? 'Sign Up' : 'Sign In'}
							</button>
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

// ============== MAIN APP ==============
function App() {
	return (
		<BrowserRouter>
			<ConfirmProvider>
			<AuthContext>
				{({ user, token, loading, login, signup, logout }) => {
					if (loading) {
						return (
							<div className="loading-container">
								<div className="loading-spinner"></div>
							</div>
						);
					}

					return (
						<>
							<Toaster position="top-right" />
							<Routes>
								<Route path="/" element={token ? <DashboardPage user={user} token={token} logout={logout} /> : <Navigate to="/auth" />} />
								<Route path="/dashboard" element={token ? <DashboardPage user={user} token={token} logout={logout} /> : <Navigate to="/auth" />} />
								<Route path="/auth" element={!token ? <AuthPage login={login} signup={signup} /> : <Navigate to="/" />} />
								<Route path="/forgot-password" element={<ForgotPasswordPage />} />
								<Route path="/reset-password" element={<ResetPasswordPage />} />
								<Route path="/analytics/:websiteId" element={token ? <AnalyticsPage user={user} token={token} logout={logout} /> : <Navigate to="/auth" />} />
								<Route path="/settings/:websiteId/:tab" element={token ? <SettingsPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/settings/:websiteId" element={token ? <SettingsPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/account" element={token ? <AccountPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/account/security" element={token ? <SecurityPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/subscription" element={token ? <SubscriptionPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/danger" element={token ? <DangerPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/choose-plan" element={token ? <ChoosePlanPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/billing-success" element={token ? <BillingSuccessPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/team-setup/:websiteId" element={token ? <TeamSetupPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/teams" element={token ? <TeamsPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/team/:teamId/settings" element={token ? <TeamSettingsPage user={user} token={token} logout={logout} /> : <Navigate to="/" />} />
								<Route path="/join-team/:token" element={<InvitationRegisterPage />} />
								<Route path="/register/invitation/:invitationToken" element={<InvitationRegisterPage login={login} />} />
								
								{/* Public Analytics Routes - No auth required */}
								<Route path="/share/:slug" element={<PublicAnalyticsPage />} />
								<Route path="/site/:domain" element={<PublicAnalyticsPage />} />

							</Routes>
						</>
					);
				}}
			</AuthContext>
			</ConfirmProvider>
		</BrowserRouter>
	);
}

export default App;
